import {
    useState,
    useEffect,
    ChangeEventHandler,
    KeyboardEventHandler,
} from 'react';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { IState } from './IState';

const useConfirm = (onClose: ICloseEvent['onClose']) => {
    const { user, lang } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        code: '',
        codeError: false,
    };
    const [state, setState] = useState(initState);

    const codeValidate = (code: string) => {
        if (!code) {
            setState((prev) => ({
                ...prev,
                codeError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                codeError: false,
            }));
            return true;
        }
    };
    const handleInputClick = () => {
        user.resetConfirmError();
        user.resetSendAlert();
        user.resetServerError();
        setState((prev) => ({
            ...prev,
            codeError: false,
        }));
    };
    const handleCodeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        handleInputClick();
        const code = e.target.value;
        setState((prev) => ({
            ...prev,
            code,
        }));
    };
    const handleSendClick = () => {
        handleInputClick();
        const { code } = state;
        const isCodeValid = codeValidate(code);
        if (isCodeValid) {
            user.confirm(code);
            setState((prev) => ({
                ...prev,
                code: '',
            }));
        }
    };
    const handleRequestConfirmClick = () => {
        handleInputClick();
        user.requestConfirm();
    };
    const handleBackClick = () => {
        user.resetEmail();
    };
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleSendClick();
                break;
        }
    };
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            handleKey(key);
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLElement;
            target.click();
        }
    };
    const handlers = {
        handleCodeChange,
        handleSendClick,
        handleRequestConfirmClick,
        handleInputClick,
        handleBackClick,
        handleKeyDown,
        handleBtnKeyDown,
    };

    useEffect(() => {
        user.resetConfirmError();
        user.resetSendAlert();
        user.resetServerError();
        user.startTimer();
        return () => {
            user.stopTimer();
            user.resetEmail();
            user.resetConfirmError();
            user.resetSendAlert();
            user.resetServerError();
        };
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        store: user,
        lang,
        state,
        handlers,
    };
};

export default useConfirm;
